/**
 * FeedbackButton Component
 *
 * Small flag button that appears on bot messages to allow users to report issues.
 * Shows filled icon if message already has feedback.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Flag, AlertCircle } from 'lucide-react';
import { useFeedback } from '../hooks/useFeedback';
import { useSettings } from '../hooks/useSettings';

const ButtonContainer = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$hasFeedback
    ? 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)'
    : 'rgba(0, 0, 0, 0.05)'};
  color: ${props => props.$hasFeedback ? 'white' : '#718096'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${props => props.$hasFeedback
      ? 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)'
      : 'rgba(0, 0, 0, 0.1)'};
    color: ${props => props.$hasFeedback ? 'white' : '#4a5568'};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FeedbackCount = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #e53e3e;
  color: white;
  font-size: 10px;
  font-weight: 600;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const Tooltip = styled(motion.div)`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1a202c;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  margin-bottom: 8px;
  z-index: 100;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #1a202c;
  }
`;

function FeedbackButton({ messageId, sessionId, onOpenModal }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const { getMessageFeedback } = useFeedback();
  const { language } = useSettings();

  useEffect(() => {
    // Check if this message already has feedback
    const checkFeedback = async () => {
      if (messageId) {
        const feedback = await getMessageFeedback(messageId);
        setFeedbackCount(feedback.length);
      }
    };
    checkFeedback();
  }, [messageId, getMessageFeedback]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (onOpenModal) {
      onOpenModal(messageId, sessionId);
    }
  };

  const tooltipText = feedbackCount > 0
    ? (language === 'ar' ? `${feedbackCount} بلاغ` : `${feedbackCount} report${feedbackCount > 1 ? 's' : ''}`)
    : (language === 'ar' ? 'الإبلاغ عن مشكلة' : 'Report issue');

  return (
    <ButtonContainer
      $hasFeedback={feedbackCount > 0}
      onClick={handleClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={tooltipText}
    >
      {feedbackCount > 0 ? <AlertCircle /> : <Flag />}
      {feedbackCount > 1 && <FeedbackCount>{feedbackCount}</FeedbackCount>}

      {showTooltip && (
        <Tooltip
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 5 }}
        >
          {tooltipText}
        </Tooltip>
      )}
    </ButtonContainer>
  );
}

export default FeedbackButton;
